import { Textarea, Button, Card } from '@artstorefronts/ui';
import { Trash2Icon, CopyIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import ImageUpload from './ImageUpload';

function PromptDataEditor({ fields, append, remove, register, update, getValues }: any) {
  const { t } = useTranslation('chat');
  const ROLE_OPTIONS = ['system', 'user', 'assistant'];

  function handleRoleChange(index: number, selectedRoles: string[]) {
    const field = fields[index];
    const role = selectedRoles[0];

    if (role) {
      const updatedField = {
        ...field,
        role,
        content: field.content || '',
      };

      update(index, updatedField);
    }
  }

  function handleImageUploadSuccess(index: number, contentIndex: number, url: string) {
    const field = fields[index];
    const currentUrl = field.content[contentIndex]?.image_url?.url;

    if (currentUrl !== url) {
      const newContent = [...field.content];
      newContent[contentIndex] = {
        ...newContent[contentIndex],
        image_url: { url },
      };
      update(index, { ...field, content: newContent });
    }
  }

  function handleImageUploadError(error: string) {
    console.error('Image upload error:', error);
  }

  function addContentItem(index: number, type: 'text' | 'image_url') {
    const field = fields[index];
    const currentFormValues = getValues(`promptData.${index}.content`);

    let newContent: any[] = [];
    if (typeof currentFormValues === 'string') {
      // Convert string content to array format
      newContent = [{ type: 'text', text: currentFormValues }];
    } else if (Array.isArray(currentFormValues)) {
      newContent = [...currentFormValues];
    } else {
      newContent = [];
    }

    // Only add text if there isn't already a text content
    if (type === 'text' && !newContent.some((item) => item.type === 'text')) {
      newContent.push({ type: 'text', text: '' });
    } else if (type === 'image_url') {
      newContent.push({ type: 'image_url', image_url: { url: '' } });
    }

    update(index, { ...field, content: newContent });
  }

  function removeContentItem(fieldIndex: number, contentIndex: number) {
    const field = fields[fieldIndex];
    const newContent = [...field.content];
    newContent.splice(contentIndex, 1);

    // If this was the last item, convert back to simple string format
    if (newContent.length === 1 && newContent[0].type === 'text') {
      update(fieldIndex, { ...field, content: newContent[0].text });
    } else {
      update(fieldIndex, { ...field, content: newContent });
    }
  }

  function duplicateField(index: number) {
    const field = fields[index];
    const fieldCopy = JSON.parse(JSON.stringify(field)); // Deep clone the field
    delete fieldCopy.id; // Remove the id so a new one will be generated
    append(fieldCopy);
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-start -mt-2">
        <Button
          type="button"
          onClick={() => append({ role: 'system', content: '' })}
          variant="outline"
          size="sm"
          className="text-xs flex items-center gap-1"
        >
          <span>{t('add-prompt-message')}</span>
        </Button>
      </div>
      {fields.map((field: any, index: number) => (
        <Card key={field.id} className="p-3 bg-zinc-100">
          <div className="space-y-3">
            <div className="flex w-full items-center justify-between gap-2">
              <div className="flex-grow">
                <div className="flex flex-wrap gap-2" role="group" aria-label={t('select-role')}>
                  {ROLE_OPTIONS.map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => handleRoleChange(index, [role])}
                      className={`px-3 py-0.5 rounded-full text-xs border border-zinc-200 transition-colors
                        ${
                          field.role === role
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400'
                        }`}
                    >
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 hover:text-red-500"
                  onClick={() => remove(index)}
                >
                  <Trash2Icon className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {typeof field.content === 'string' ? (
                <Textarea {...register(`promptData.${index}.content`)} className="text-sm min-h-[60px]" rows={8} />
              ) : (
                <>
                  {field.content?.map((contentItem: any, contentIndex: number) => (
                    <div key={contentIndex} className="relative pl-3 border-l-2 border-zinc-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-zinc-500">
                          {contentItem.type === 'text' ? t('text') : t('image-label')}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 hover:text-red-500"
                          onClick={() => removeContentItem(index, contentIndex)}
                        >
                          <span className="text-xs font-light text-zinc-500 mr-2">{t('remove-content')}</span>
                          <Trash2Icon className="h-4 w-4 text-zinc-500" />
                        </Button>
                      </div>

                      {contentItem.type === 'text' && (
                        <Textarea
                          {...register(`promptData.${index}.content.${contentIndex}.text`)}
                          className="text-sm min-h-[60px]"
                          rows={8}
                        />
                      )}

                      {contentItem.type === 'image_url' && (
                        <ImageUpload
                          onUploadSuccess={(url) => handleImageUploadSuccess(index, contentIndex, url)}
                          onUploadError={handleImageUploadError}
                          existingImageUrl={contentItem.image_url?.url || ''}
                          className="w-32"
                        />
                      )}
                    </div>
                  ))}
                </>
              )}

              <div className="flex gap-2 mt-2 justify-between">
                <div className="flex gap-2">
                  {typeof field.content !== 'string' && !field.content?.some((item: any) => item.type === 'text') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => addContentItem(index, 'text')}
                    >
                      {t('add-text')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => addContentItem(index, 'image_url')}
                  >
                    {t('add-image')}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs flex items-center gap-1 text-zinc-500 hover:text-zinc-900"
                  onClick={() => duplicateField(index)}
                >
                  <CopyIcon className="h-3 w-3" />
                  <span>{t('duplicate')}</span>
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default PromptDataEditor;
